import { z } from "zod";

import { Form } from "@/components/ui/form";
import i18n from "@/i18n";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Input,
} from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import {
  CheckboxField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  dateSchema,
  formatDate,
  parseToDateValue,
  parseYes,
} from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";

const FormSchema = z.object({
  strategy_id: z.coerce.number().nullable().optional(),
  portfolio_id: z.coerce.number().nullable().optional(),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  instrument_type: z.string().nullable().optional(),
  unit_price: z.coerce.number().nullable().optional(),
  cost_price: z.coerce.number().nullable().optional(),
  accrued_interest: z.coerce.number().nullable().optional(),
  percentage_price: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
  premium: z.coerce.number().nullable().optional(),
  discount: z.coerce.number().nullable().optional(),
  duration: z.coerce.number().nullable().optional(),
  sensitivity: z.coerce.number().nullable().optional(),
  credit_quality_step: z.coerce.number().int().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export function G4DynamicDataEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {i18n.t("g4_dynamic_data.entry", { defaultValue: "Entry" })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="strategy_id"
                    label={i18n.t("g4_dynamic_data.columns.strategy_name", {
                      ns: "tables",
                      defaultValue: "Strategy",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="portfolio_id"
                    label={i18n.t("g4_dynamic_data.columns.portfolio_name", {
                      ns: "tables",
                      defaultValue: "Portfolio",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={i18n.t("g4_dynamic_data.columns.date", {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="instrument_type"
                    label={i18n.t("g4_dynamic_data.columns.instrument_type", {
                      ns: "tables",
                      defaultValue: "Instrument type",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="unit_price"
                    label={i18n.t("g4_dynamic_data.columns.unit_price", {
                      ns: "tables",
                      defaultValue: "Unit price",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="cost_price"
                    label={i18n.t("g4_dynamic_data.columns.cost_price", {
                      ns: "tables",
                      defaultValue: "Cost price",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="accrued_interest"
                    label={i18n.t("g4_dynamic_data.columns.accrued_interest", {
                      ns: "tables",
                      defaultValue: "Accrued interest",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="percentage_price"
                    label={i18n.t("g4_dynamic_data.columns.percentage_price", {
                      ns: "tables",
                      defaultValue: "Percentage price",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="premium"
                    label={i18n.t("g4_dynamic_data.columns.premium", {
                      ns: "tables",
                      defaultValue: "Premium",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="discount"
                    label={i18n.t("g4_dynamic_data.columns.discount", {
                      ns: "tables",
                      defaultValue: "Discount",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="duration"
                    label={i18n.t("g4_dynamic_data.columns.duration", {
                      ns: "tables",
                      defaultValue: "Duration",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="sensitivity"
                    label={i18n.t("g4_dynamic_data.columns.sensitivity", {
                      ns: "tables",
                      defaultValue: "Sensitivity",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name="credit_quality_step"
                    allowDecimal={false}
                    label={i18n.t(
                      "g4_dynamic_data.columns.credit_quality_step",
                      {
                        ns: "tables",
                        defaultValue: "Cqs",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <Divider />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={i18n.t("technical_info.id", {
                      defaultValue: "Id",
                    })}
                    value={id.toString()}
                    readOnly
                  />
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={i18n.t("technical_info.created_at", {
                      defaultValue: "Créé le",
                    })}
                    value={formatDate(form.watch("created_at"))}
                    readOnly
                  />
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={i18n.t("technical_info.updated_at", {
                      defaultValue: "Mis à jour le",
                    })}
                    value={formatDate(form.watch("updated_at"))}
                    readOnly
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={i18n.t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, [], {}, table_name)
              : i18n.t("no_changes")}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
